
import { useState } from "react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Wrench } from "lucide-react";
import { nl } from "date-fns/locale";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface MaintenancePlannerProps {
  vehicleId: string;
  showHistory?: boolean;
}

interface MaintenanceAppointment {
  id: string;
  requested_date: string;
  requested_time: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';
  description: string | null;
  created_at: string;
  mechanic_notes: string | null;
  pickup_requested: boolean;
  pickup_cost: number | null;
  washing_requested: boolean;
  detailing_requested: boolean;
}

export const MaintenancePlanner = ({ vehicleId, showHistory = false }: MaintenancePlannerProps) => {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [pickupRequested, setPickupRequested] = useState(false);
  const [washingRequested, setWashingRequested] = useState(false);
  const [detailingRequested, setDetailingRequested] = useState(false);
  const { toast } = useToast();

  // Beschikbare tijdsloten (in een echte applicatie zou dit van de backend komen)
  const timeSlots = [
    { time: "09:00", available: true },
    { time: "09:30", available: true },
    { time: "10:00", available: true },
    { time: "10:30", available: false },
    { time: "11:00", available: true },
    { time: "11:30", available: true },
    { time: "12:00", available: false },
    { time: "13:00", available: true },
    { time: "13:30", available: true },
    { time: "14:00", available: true },
    { time: "14:30", available: true },
    { time: "15:00", available: true },
    { time: "15:30", available: true },
    { time: "16:00", available: true },
    { time: "16:30", available: true },
  ];

  const { data: appointments, refetch } = useQuery({
    queryKey: ["maintenance-appointments", vehicleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicle_maintenance")
        .select("*")
        .eq("vehicle_id", vehicleId)
        .order("requested_date", { ascending: false });

      if (error) throw error;
      return data as MaintenanceAppointment[];
    },
  });

  const requestAppointment = async () => {
    if (!selectedTime) return;

    try {
      const { error } = await supabase
        .from("vehicle_maintenance")
        .insert({
          vehicle_id: vehicleId,
          requested_date: format(selectedDate, "yyyy-MM-dd"),
          requested_time: selectedTime,
          status: "pending",
          pickup_requested: pickupRequested,
          washing_requested: washingRequested,
          detailing_requested: detailingRequested,
          description: "Regulier onderhoud"
        });

      if (error) throw error;

      toast({
        title: "Afspraak aangevraagd",
        description: "Je onderhoudsafspraak is succesvol aangevraagd en wordt beoordeeld door een monteur.",
      });

      setSelectedTime(null);
      setPickupRequested(false);
      setWashingRequested(false);
      setDetailingRequested(false);
      refetch();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij aanvragen",
        description: error.message,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-[#B1A06D]" />
          <CardTitle className="text-xl font-semibold">Onderhoudsplanner</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border">
          <div className="flex flex-col sm:flex-row">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(newDate) => {
                if (newDate) {
                  setSelectedDate(newDate);
                  setSelectedTime(null);
                }
              }}
              className="p-2"
              disabled={[{ before: today }]}
              locale={nl}
            />
            <div className="w-full p-4 border-t sm:border-t-0 sm:border-l">
              <div className="h-5 mb-3">
                <p className="text-sm font-medium">
                  {format(selectedDate, "EEEE d MMMM", { locale: nl })}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {timeSlots.map(({ time, available }) => (
                  <Button
                    key={time}
                    variant={selectedTime === time ? "default" : "outline"}
                    size="sm"
                    className="w-full"
                    onClick={() => setSelectedTime(time)}
                    disabled={!available}
                  >
                    {time}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="pickup"
              checked={pickupRequested}
              onCheckedChange={setPickupRequested}
            />
            <Label htmlFor="pickup" className="text-sm text-gray-600">
              Auto ophalen en terugbrengen (kosten worden berekend op basis van afstand)
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="washing"
              checked={washingRequested}
              onCheckedChange={setWashingRequested}
            />
            <Label htmlFor="washing" className="text-sm text-gray-600">
              Auto volledig wassen (binnen en buiten)
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="detailing"
              checked={detailingRequested}
              onCheckedChange={setDetailingRequested}
            />
            <Label htmlFor="detailing" className="text-sm text-gray-600">
              Auto detailen
            </Label>
          </div>
        </div>

        <Button
          className="w-full"
          onClick={requestAppointment}
          disabled={!selectedTime}
        >
          Afspraak aanvragen
        </Button>
      </CardContent>
    </Card>
  );
};
